import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Info as InfoIcon, PlayCircleFilled as PlayCircleFilledIcon } from '@mui/icons-material';
import FlagIcon from '@mui/icons-material/Flag';

import { classNames, getSemaphoreColor } from "~/utils/utils";
import { useTrans } from '~/hooks/useTrans';
import { getLink } from "~/providers/route";
import { IAdminSearchProject, IAdminSearchProjectFilter } from "~/providers/admin-interfaces";
import Loader from '~/components/Loader';
import { SEMAPHORE_CONSUMPTION } from "~/const";
import MyCheckbox from "~/mui/MyCheckbox";
import SortArrows, { TSortArrowSelect } from "~/components/SortArrows";

import "./style.less";

interface IAdminProjectsTable {
	filter: IAdminSearchProjectFilter;
	loading: boolean;
	items: Array<IAdminSearchProject>;
	onSortUpdate: (newFilter: IAdminSearchProjectFilter) => void;
	selectedId?: number;
}

const COLUMNS = 9;

export default function AdminProjectsTable({
	filter,
	loading = true,
	items = [],
	onSortUpdate,
	selectedId = -1,
}: IAdminProjectsTable) {
	const { getSemaphoreTitle, trans, transProjectBusinessPlan, formatCredits, formatPennyPrice, getVIPInfo, transBillingMethodShort } = useTrans();

	function getSelect(filterName: keyof IAdminSearchProjectFilter): TSortArrowSelect {
		if (filterName in filter) {
			return filter[filterName] === "ASC"
				? "up"
				: "down";
		}

		return "none";
	}

	function onSelectClick(arrow: TSortArrowSelect, filterName: keyof IAdminSearchProjectFilter) {
		onSortUpdate({
			...arrow === "none"
				? {}
				: {
					[filterName]: arrow === "up"
						? "ASC"
						: "DESC",
				},
		});
	}

	function getProjectAction(project: IAdminSearchProject) {
		if (
			(project.actionDescription && !project.actionValidFrom)
			|| (project.actionValidFrom && new Date(project.actionValidFrom).getTime() < Date.now())
		) {
			return <Tooltip title={project.actionDescription || "Popis události"}>
				<FlagIcon />
			</Tooltip>;
		}

		return null;
	}

	useEffect(() => {
		if (selectedId >= 0) {
			const elem = document.querySelector(`.projectTableItem[data-id="${selectedId}"]`);

			elem && elem.scrollIntoView();
		}
	}, []);

	return <TableContainer component={Paper}>
		<Table sx={{ minWidth: 650 }} aria-label="table">
			<TableHead>
				<TableRow>
					<TableCell sx={{ fontWeight: "bold" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("projectSettingsPage.idProject") }
							<SortArrows select={getSelect("sortByInternalName")} onClick={arrow => onSelectClick(arrow, "sortByInternalName")} />
						</div>
					</TableCell>
					<TableCell>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("allProjectsPage.projectState") }
							<SortArrows select={getSelect("sortByCanConsume")} onClick={arrow => onSelectClick(arrow, "sortByCanConsume")} />
						</div>
					</TableCell>
					<TableCell>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("projectFinancialPage.tarif.title") }
							<SortArrows select={getSelect("sortByBusinessPlanType")} onClick={arrow => onSelectClick(arrow, "sortByBusinessPlanType")} />
						</div>
					</TableCell>
					<TableCell>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("allProjectsPage.paidConsumption") }
							<SortArrows select={getSelect("sortByBilledConsumption")} onClick={arrow => onSelectClick(arrow, "sortByBilledConsumption")} />
						</div>
					</TableCell>
					<TableCell>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("allProjectsPage.actualConsumption") }
							<SortArrows select={getSelect("sortByCurrentMonthConsumption")} onClick={arrow => onSelectClick(arrow, "sortByCurrentMonthConsumption")} />
						</div>
					</TableCell>
					<TableCell>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("allProjectsPage.lastConsumption") }
							<SortArrows select={getSelect("sortByPreviousMonthConsumption")} onClick={arrow => onSelectClick(arrow, "sortByPreviousMonthConsumption")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("table.semaphore") }
							<SortArrows select={getSelect("sortByLastConsumptionDate")} onClick={arrow => onSelectClick(arrow, "sortByLastConsumptionDate")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("adminProjectsPage.currentMonthAmountBilled") }
							<SortArrows select={getSelect("sortByCurrentMonthAmountBilled")} onClick={arrow => onSelectClick(arrow, "sortByCurrentMonthAmountBilled")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort">
							{ trans("adminProjectsPage.previousMonthAmountBilled") }
							<SortArrows select={getSelect("sortByPreviousMonthAmountBilled")} onClick={arrow => onSelectClick(arrow, "sortByPreviousMonthAmountBilled")} />
						</div>
					</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{ items.map(project => (<TableRow key={project.id} data-id={project.id}>
					<TableCell className="adminProjectsTable__projectName">
						<Link to={getLink("project-dashboard", true, { projectId: project.id })} className={classNames(["projectTableItem__name", project.archived ? "projectTableItem__archived" : ""])}>
							{getProjectAction(project)} { project.internalName }
						</Link>
					</TableCell>
					<TableCell className="text-align-center">
						{ project.canConsume
							? <span style={{ color: "var(--dark-green-text-color)" }}>
								{ trans("allProjectsPage.projectStateActive") }
							</span>
							: <span style={{ color: "var(--red-text-color)", position: "relative" }}>
								{ trans("allProjectsPage.projectStateInactive") }
								<Tooltip title={project.forbiddenConsumptionReason} sx={{ position: "absolute", top: "-3px", marginLeft: "5px", cursor: "pointer" }}>
									<InfoIcon />
								</Tooltip>
							</span> }
					</TableCell>
					<TableCell className="text-align-center">
						{ transProjectBusinessPlan(project.businessPlanType) }
						{ project.businessPlanType === "vip" && <span className="projectTableItem__vipInfo">
							{ getVIPInfo(project) }
						</span> }
					</TableCell>
					<TableCell>
						<div className="projectTableItem__billingMethodCell">
							{ project.billedConsumption ? <MyCheckbox checked={true} label="" onChange={() => {}} className="adminProjectsTable__checkbox" /> : null }
							{ project.billedConsumption && <span className="projectTableItem__billingMethod">
								{ transBillingMethodShort(project.billingMethod) }
							</span> }
						</div>
					</TableCell>
					<TableCell className="text-align-center">
						{ formatCredits(project.currentMonthConsumption) }
					</TableCell>
					<TableCell className="text-align-center">
						{ formatCredits(project.previousMonthConsumption) }
					</TableCell>
					<TableCell className="text-align-center">
						<Tooltip title={getSemaphoreTitle(SEMAPHORE_CONSUMPTION, project.lastConsumptionDate)} sx={{ marginLeft: "5px", position: "relative", top: "6px" }}>
							<PlayCircleFilledIcon sx={{ fill: getSemaphoreColor(SEMAPHORE_CONSUMPTION, project.lastConsumptionDate), cursor: "pointer" }} />
						</Tooltip>
					</TableCell>
					<TableCell className="text-align-center">
						{ formatPennyPrice(project.currentMonthAmountBilled) }
					</TableCell>
					<TableCell className="text-align-center">
						{ formatPennyPrice(project.previousMonthAmountBilled) }
					</TableCell>
				</TableRow>)) }
				{ items.length === 0 && <TableRow>
					<TableCell colSpan={COLUMNS} style={{ textAlign: "center" }}>
						{ loading && <Loader msg={trans("loadingData")} /> }
						{ !loading && trans("emptyTable") }
					</TableCell>
				</TableRow> }
			</TableBody>
		</Table>
	</TableContainer>;
}
